import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQueryWithHeaders } from '../api';
import { endpoints } from '../endpoints';

export interface PracticeConfiguration {
  id: number;
  practiceId: number;
  forwardingEmail: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  taxIdentificationNumber: string;
  nationalProviderId: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  zipcode: string;
}

export interface ConfigurePracticeRequest {
  forwardingEmail?: string;
  taxIdentificationNumber?: string;
  nationalProviderId?: string;
  address1?: string;
  address2?: string | null;
  city?: string;
  state?: string;
  zipcode?: string;
}

// Hook to get practice configuration
export const useGetPracticeConfiguration = () => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  return useQuery<PracticeConfiguration>(
    [endpoints.rcmZuubOnboarding.configuration],
    () =>
      makeAPIRequestReactQueryWithHeaders<PracticeConfiguration>(
        endpoints.rcmZuubOnboarding.configuration,
        undefined,
        {
          Org: currentOrg,
          Practice: currentPractice,
        },
        'GET',
      ),
    {
      enabled: Boolean(currentPractice && currentOrg),
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};

// Hook to update practice configuration
export const useConfigurePractice = () => {
  const queryClient = useQueryClient();
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  return useMutation<PracticeConfiguration, Error, ConfigurePracticeRequest>(
    (data) =>
      makeAPIRequestReactQueryWithHeaders<PracticeConfiguration>(
        endpoints.rcmZuubOnboarding.configuration,
        undefined,
        {
          Org: currentOrg,
          Practice: currentPractice,
        },
        'POST',
        {
          data,
        },
      ),
    {
      onSuccess: () => {
        // Invalidate and refetch the configuration
        queryClient.invalidateQueries([endpoints.rcmZuubOnboarding.configuration]);
      },
    },
  );
};

